import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Link from '@mui/material/Link'
import Divider from '@mui/material/Divider'
import MuiButton from '@mui/material/Button'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PageBanner from '../components/common/PageBanner'
import Container from '../components/common/Container'
import SectionIntro from '../components/common/SectionIntro'
import { cdnImage } from '../config/env'

export default function About() {
  useEffect(() => {
    document.title = 'About — Future of Farm Food Library'
  }, [])

  return (
    <>
      <PageBanner
        overline="About this project"
        headline="About the Future of Farm Food Library"
        subtext="A knowledge infrastructure project for the regenerative agriculture transition in South Africa."
      />

      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'background.default' }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <SectionIntro
                overline="The project"
                heading="Why this library and directory exists"
              />
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                The transition to regenerative agriculture in South Africa is urgent, evidence-rich, and deeply fragmented. Farmers, food processors and retailers, investors, customers, researchers, policymakers, and ecosystem partners are all working on pieces of the puzzle — but shared knowledge infrastructure has been largely absent.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                The Future of Farm Food Library and Directory is designed to address that gap: a curated, accessible, and growing repository of research, ecosystem mapping, and practical evidence to support better decisions across the sector.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                The library and directory are built around three core functions: curating the research evidence base, mapping the ecosystem of actors and organisations, and providing a pathway for practitioners to contribute their knowledge and presence to the shared resource.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                This is a living project. It will grow as the ecosystem grows, and as more organisations, researchers, and farmers contribute to it.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={cdnImage('12-ecosystem-option-soil-research-sphere_2.png')}
                alt="Soil and field research scene inside a glass sphere"
                loading="eager"
                sx={{
                  width: 'auto',
                  height: { xs: 260, sm: 320, md: 390 },
                  maxWidth: { xs: '100%', md: 420 },
                  objectFit: 'contain',
                  display: 'block',
                  flexShrink: 0,
                  filter: 'drop-shadow(0 20px 42px rgba(11, 32, 39, 0.14))',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Partners */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'rgba(138, 154, 91, 0.1)' }}>
        <Container>
          <SectionIntro
            overline="Supported by"
            heading="Our Partners"
            body="The Future of Farm Food Library is made possible through the support and leadership of two organisations with complementary missions."
          />
          <Grid container spacing={4}>
            {/* ACF */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(27, 31, 29, 0.08)',
                  borderRadius: 3,
                  borderTop: '5px solid',
                  borderTopColor: 'primary.main',
                  boxShadow: '0 16px 36px rgba(27, 31, 29, 0.1)',
                }}
              >
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  African Climate Foundation
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  The African Climate Foundation (ACF) has generously funded the research and other associated costs required to establish this website.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  ACF works to ensure that Africa's climate priorities — rooted in adaptation, food security, and economic resilience — are reflected in global climate policy and finance frameworks. Their work spans land use, food systems, finance, and energy, always anchored in African priorities and African expertise.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  Within the food system, ACF recognises that a transition to regenerative and agroecological practices is both a climate imperative and a development opportunity for the continent. This library and directory represent part of their investment in the evidence base and coordination infrastructure needed to support that transition.
                </Typography>
                <MuiButton
                  component={Link}
                  href="https://africanclimatefoundation.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="primary"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                >
                  Visit African Climate Foundation
                </MuiButton>
              </Paper>
            </Grid>

            {/* Impact Amplifier */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(27, 31, 29, 0.08)',
                  borderRadius: 3,
                  borderTop: '5px solid',
                  borderTopColor: 'primary.main',
                  boxShadow: '0 16px 36px rgba(27, 31, 29, 0.1)',
                }}
              >
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Impact Amplifier
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  Impact Amplifier is a social innovation ecosystem builder that supports inclusive, climate-resilient economic growth by aligning finance, learning, markets, and coordination for systemic impact across Southern Africa.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  Rather than operating as a single-intervention funder or programme provider, Impact Amplifier works to strengthen the connective tissue of impact ecosystems — building shared infrastructure, facilitating learning networks, and ensuring that the right actors are connected to the right resources at the right time.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                  Within regenerative agriculture, Impact Amplifier brings deep expertise in ecosystem coordination and a clear-eyed view of where collaboration is most needed: connecting farmers with extension services, linking investors with proof-of-concept opportunities, and building the institutional knowledge that makes systemic change possible.
                </Typography>
                <MuiButton
                  component={Link}
                  href="https://impactamplifier.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="primary"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                >
                  Visit Impact Amplifier
                </MuiButton>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}
